/** Import data models from a JSON export: pick the file, see what it holds, import, read the report. */
import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Api, type ModelsImportReport } from '../lib/api.ts';
import { cx, IconButton } from '../design/primitives.tsx';
import { CloseIcon } from './icons.tsx';
import { Button, useToast } from './ui.tsx';

interface Doc { format?: string; models?: { name: string; versions?: unknown[]; deployed?: Record<string, number> }[] }

export function ImportModels({ tid, onClose, onDone }: { tid: string; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [doc, setDoc] = useState<Doc>();
  const [fileName, setFileName] = useState('');
  const [problem, setProblem] = useState<string>();
  const [deploy, setDeploy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState<ModelsImportReport>();

  const pick = async (file: File | undefined) => {
    setReport(undefined);
    setProblem(undefined);
    setDoc(undefined);
    if (!file) return;
    setFileName(file.name);
    try {
      const parsed = JSON.parse(await file.text()) as Doc;
      if (parsed.format !== 'messagevault-models' || !Array.isArray(parsed.models)) throw new Error('This is not a MessageVault data model export');
      setDoc(parsed);
    } catch (e) {
      setProblem(e instanceof Error ? e.message : String(e));
    }
  };
  const go = async () => {
    if (!doc) return;
    setBusy(true);
    setProblem(undefined);
    try {
      const r = await Api.importModels(tid, doc, deploy, confirm);
      setReport(r);
      toast(`${r.models.filter((m) => m.result === 'created' || m.result === 'updated').length} of ${r.models.length} models imported`);
      onDone();
    } catch (e) {
      setProblem(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const envNames = [...new Set((doc?.models ?? []).flatMap((m) => Object.keys(m.deployed ?? {})))];

  return (
    <Dialog.Root open onOpenChange={(open) => { if (!open) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px]" />
        <Dialog.Content aria-describedby={undefined}
          className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(640px,94vw)] max-h-[88vh] overflow-y-auto flex flex-col gap-4 bg-n2 text-n10 rounded-card shadow-float p-5 outline-none mv-pop">
          <div className="flex items-start gap-3">
            <div className="flex-1 flex flex-col gap-1">
              <Dialog.Title className="m-0 text-body font-semibold">Import data models</Dialog.Title>
              <p className="m-0 text-small text-n8">
                Models are matched by name and environments by name. Versions are immutable: a model only grows by newer versions, and a version that differs from the one here is a conflict that is skipped.
              </p>
            </div>
            <Dialog.Close asChild><IconButton label="Close"><CloseIcon size={16} /></IconButton></Dialog.Close>
          </div>

          <label className="flex flex-col gap-1.5 text-small">
            <span className="text-n8">JSON file from “Export JSON”</span>
            <input type="file" accept="application/json,.json" onChange={(e) => void pick(e.target.files?.[0])} className="text-small" />
          </label>
          {problem && <p className="m-0 text-small text-fail">{problem}</p>}

          {doc && !report && (
            <div className="flex flex-col gap-3">
              <p className="m-0 text-small text-n9">{fileName}: {doc.models!.length} model{doc.models!.length === 1 ? '' : 's'} ({doc.models!.map((m) => m.name).slice(0, 6).join(', ')}{doc.models!.length > 6 ? '…' : ''}).</p>
              <label className="flex items-center gap-2 text-small cursor-pointer">
                <input type="checkbox" checked={deploy} onChange={(e) => setDeploy(e.target.checked)} />
                Also deploy the versions to the environments of the same name{envNames.length ? ` (${envNames.join(', ')})` : ''}
              </label>
              {deploy && (
                <label className="flex items-center gap-2 text-small cursor-pointer">
                  <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} />
                  I confirm deploying to protected environments
                </label>
              )}
            </div>
          )}

          {report && (
            <ul className="m-0 p-0 list-none flex flex-col gap-2">
              {report.models.map((m) => (
                <li key={m.name} className="rounded-chip bg-n3 px-3 py-2 text-small flex flex-col gap-0.5">
                  <span className="flex items-center gap-2">
                    <span className="font-medium">{m.name}</span>
                    <span className={cx('text-micro', m.result === 'conflict' ? 'text-fail' : 'text-n8')}>{m.result}</span>
                    {m.versionsAdded > 0 && <span className="text-micro text-n8">+{m.versionsAdded} version{m.versionsAdded === 1 ? '' : 's'}</span>}
                    {m.deployed.length > 0 && <span className="text-micro text-n8">deployed: {m.deployed.join(', ')}</span>}
                  </span>
                  {m.notes.map((n) => <span key={n} className="text-micro text-n8">{n}</span>)}
                </li>
              ))}
            </ul>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>{report ? 'Close' : 'Cancel'}</Button>
            {!report && <Button variant="primary" disabled={!doc || busy} onClick={() => void go()}>Import</Button>}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
